import React, { useState } from 'react';
import styled, { keyframes } from 'styled-components';

export const HOW_HEARD_OPTIONS = [
  'Instagram',
  'Facebook',
  'YouTube',
  'Word of mouth',
  'Previous visit',
  'Google',
  'Sky TV',
  'Radio',
  'Mobile apps',
  'Newsletter',
  'Flyer',
  'Digital advertising screen',
  'Other',
];

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`;

const Wrap = styled.div`
  width: min(640px, 100%);
  padding: 0 1rem 0.25rem;
  animation: ${fadeIn} 0.35s ease;
  text-align: center;

  @media (max-width: 520px) {
    padding: 0 0.75rem 0.25rem;
  }
`;

const Status = styled.p`
  margin: 0 0 0.35rem;
  font-size: 0.72rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: rgba(16, 29, 64, 0.55);
  font-weight: 600;
`;

const Title = styled.h2`
  margin: 0 0 0.35rem;
  font-family: ${props => props.theme.fonts.main};
  font-size: clamp(1.2rem, 5vw, 1.75rem);
  color: ${props => props.theme.colors.primary};
  font-weight: 700;
  line-height: 1.2;
`;

const Subtitle = styled.p`
  margin: 0 0 1rem;
  font-size: 0.9rem;
  color: rgba(16, 29, 64, 0.65);
  line-height: 1.4;

  @media (max-width: 520px) {
    margin-bottom: 0.75rem;
    font-size: 0.84rem;
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.55rem;
  margin-bottom: 0.85rem;

  @media (max-width: 520px) {
    gap: 0.45rem;
    margin-bottom: 0.7rem;
  }
`;

const Option = styled.button`
  border: 1.5px solid ${props => (props.$active ? props.theme.colors.primary : 'rgba(16, 29, 64, 0.14)')};
  background: ${props => (props.$active ? props.theme.colors.primary : 'white')};
  color: ${props => (props.$active ? 'white' : props.theme.colors.primary)};
  border-radius: 10px;
  padding: 0.75rem 0.7rem;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s ease;
  text-align: center;
  min-height: 44px;
  -webkit-tap-highlight-color: transparent;

  @media (max-width: 520px) {
    padding: 0.62rem 0.45rem;
    font-size: 0.8rem;
    border-radius: 8px;
    line-height: 1.2;
  }

  &:hover {
    border-color: ${props => props.theme.colors.primary};
  }
`;

const OtherInput = styled.input`
  width: 100%;
  box-sizing: border-box;
  margin: 0 0 0.85rem;
  padding: 0.8rem 0.9rem;
  border-radius: 10px;
  border: 1.5px solid rgba(16, 29, 64, 0.18);
  font-size: 16px;
  font-family: inherit;

  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
  }
`;

const Actions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  align-items: center;
  position: sticky;
  bottom: 0;
  padding-top: 0.35rem;
  background: linear-gradient(180deg, rgba(248, 247, 244, 0) 0%, ${props => props.theme.colors.offWhite} 28%);
`;

const ContinueBtn = styled.button`
  width: 100%;
  max-width: 320px;
  border: none;
  border-radius: 10px;
  padding: 0.9rem 1.25rem;
  background: ${props => props.theme.colors.accent};
  color: white;
  font-size: 1rem;
  font-weight: 700;
  cursor: ${props => (props.disabled ? 'not-allowed' : 'pointer')};
  opacity: ${props => (props.disabled ? 0.5 : 1)};
  min-height: 48px;
`;

const SkipBtn = styled.button`
  border: none;
  background: transparent;
  color: rgba(16, 29, 64, 0.55);
  font-size: 0.9rem;
  cursor: pointer;
  text-decoration: underline;
  padding: 0.55rem;
  min-height: 44px;
`;

const ProgressNote = styled.p`
  margin: 0.35rem 0 0;
  font-size: 0.78rem;
  color: rgba(16, 29, 64, 0.5);
`;

/**
 * Shown while a booking is being confirmed.
 * Optional — skipping or ignoring never blocks the booking.
 * onComplete({ source, otherText }) or onComplete(null) for skip.
 */
const HowHeardSurvey = ({ confirming = true, onComplete }) => {
  const [source, setSource] = useState('');
  const [otherText, setOtherText] = useState('');

  const canContinue =
    !!source && (source !== 'Other' || otherText.trim().length > 0);

  const choose = (opt) => {
    setSource(opt);
    if (opt !== 'Other') {
      onComplete({ source: opt, otherText: '' });
    }
  };

  return (
    <Wrap>
      <Status>{confirming ? 'Confirming your booking…' : 'Almost done'}</Status>
      <Title>How did you hear about us?</Title>
      <Subtitle>Optional — you can skip this. Your booking is already being confirmed.</Subtitle>

      <Grid>
        {HOW_HEARD_OPTIONS.map((opt) => (
          <Option
            key={opt}
            type="button"
            $active={source === opt}
            onClick={() => choose(opt)}
          >
            {opt}
          </Option>
        ))}
      </Grid>

      {source === 'Other' && (
        <OtherInput
          type="text"
          value={otherText}
          onChange={(e) => setOtherText(e.target.value)}
          placeholder="Please tell us how you heard about us"
          autoFocus
          maxLength={200}
        />
      )}

      <Actions>
        {source === 'Other' && (
          <ContinueBtn
            type="button"
            disabled={!canContinue}
            onClick={() =>
              onComplete({
                source,
                otherText: otherText.trim(),
              })
            }
          >
            Continue
          </ContinueBtn>
        )}
        <SkipBtn type="button" onClick={() => onComplete(null)}>
          Skip
        </SkipBtn>
        {confirming && (
          <ProgressNote>Your booking will go through either way.</ProgressNote>
        )}
      </Actions>
    </Wrap>
  );
};

export default HowHeardSurvey;
